/**
 * Money handling for the store.
 *
 * Every amount in this codebase is an integer number of minor units (pence for
 * GBP). Floating point never touches a price: `0.1 + 0.2 !== 0.3` is a rounding
 * bug waiting to happen on a cart total, and Shopify already stores prices this
 * way, so the migration carries integers straight across.
 */
const CURRENCY_LOCALE = {
    GBP: 'en-GB',
    EUR: 'en-IE',
    USD: 'en-US',
};
export function money(amount, currency = 'GBP') {
    return { amount: assertMinor(amount), currency };
}
export function assertMinor(value) {
    if (!Number.isInteger(value)) {
        throw new TypeError(`Money amounts must be integer minor units, received ${value}. ` +
            `Use poundsToMinor() to convert a decimal amount.`);
    }
    return value;
}
/**
 * Parse a decimal amount of major units ("19.99") into pence (1999).
 *
 * The digits are read directly rather than multiplying by 100, because scaling
 * through a float is not reliable in either direction: `parseFloat("19.99") *
 * 100` is 1998.9999999999998 (rounds down to the right answer by luck), while
 * `1.005 * 100` is 100.49999999999999 and rounds to 100 instead of 101. Reading
 * the decimal places as text sidesteps the representation entirely.
 */
export function poundsToMinor(value) {
    const text = typeof value === 'string' ? value.trim() : String(value);
    const match = /^(-?)(\d*)(?:\.(\d+))?$/.exec(text);
    if (!match || (match[2] === '' && match[3] === undefined)) {
        throw new TypeError(`Cannot parse "${value}" as a monetary amount`);
    }
    const [, sign, whole = '', fraction = ''] = match;
    const pence = (fraction + '00').slice(0, 2);
    // The third decimal place decides the rounding; anything beyond it cannot
    // change a half-up result.
    const roundUp = Number(fraction[2] ?? '0') >= 5;
    const magnitude = Number(whole || '0') * 100 + Number(pence) + (roundUp ? 1 : 0);
    return sign === '-' ? -magnitude : magnitude;
}
export function minorToPounds(amount) {
    return amount / 100;
}
/**
 * Apply a percentage discount, rounding half-up to the nearest penny.
 *
 * Half-up (rather than JS's `Math.round`, which is half-up only for positives)
 * is the conventional retail rounding: 15% off £21.00 is £3.15 exactly, but
 * 15% off £19.00 is £2.85 from 2.8499…, and shoppers expect the penny in their
 * favour to be consistent, not to drift with the sign.
 */
export function percentOf(amount, percent) {
    if (percent < 0 || percent > 100) {
        throw new RangeError(`Discount percent must be 0-100, received ${percent}`);
    }
    const raw = (assertMinor(amount) * percent) / 100;
    return roundHalfUp(raw);
}
export function roundHalfUp(value) {
    return Math.sign(value) * Math.round(Math.abs(value));
}
/** Clamp to a non-negative amount — a discount must never create a credit. */
export function clampNonNegative(amount) {
    return amount < 0 ? 0 : amount;
}
export function sumMinor(amounts) {
    return amounts.reduce((total, amount) => total + assertMinor(amount), 0);
}
/**
 * Split an amount across n lines proportionally to their weights, guaranteeing
 * the parts sum exactly back to `amount`.
 *
 * Used to spread an order-level discount (e.g. "£5 off your order") back over
 * the individual lines so refunds and per-line reporting stay consistent. The
 * naive approach — rounding each share independently — loses or gains pennies;
 * this hands any remainder to the largest weights, one penny at a time.
 */
export function allocateProportionally(amount, weights) {
    assertMinor(amount);
    const totalWeight = sumMinor(weights);
    if (totalWeight <= 0) {
        return weights.map(() => 0);
    }
    const shares = weights.map((w) => Math.floor((amount * w) / totalWeight));
    let remainder = amount - shares.reduce((a, b) => a + b, 0);
    // Distribute the leftover pennies to the largest weights first, so the
    // rounding favours the biggest lines rather than whichever came first.
    const order = weights
        .map((w, i) => ({ w, i }))
        .sort((a, b) => b.w - a.w || a.i - b.i);
    for (let k = 0; remainder > 0 && k < order.length; k = (k + 1) % order.length) {
        const entry = order[k];
        if (entry === undefined)
            break;
        shares[entry.i] = (shares[entry.i] ?? 0) + 1;
        remainder -= 1;
        if (remainder === 0)
            break;
    }
    return shares;
}
export function formatMoney(amount, currency = 'GBP', options = {}) {
    if (options.showZeroAsFree && amount === 0)
        return 'Free';
    return new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
        style: 'currency',
        currency,
    }).format(minorToPounds(amount));
}
//# sourceMappingURL=money.js.map